import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AttachmentPreviewService, StatementAttachment } from '../../../services/attachment-preview.service';
import { ModalDocumentViewerComponent } from '../../../modals/modal-document-viewer/modal-document-viewer.component';

export const DEFAULT_VISIBLE_DOCUMENTS = 3;

export function sortStatementAttachments(attachments: StatementAttachment[]): StatementAttachment[] {
  return [...attachments].sort((a, b) => {
    const primary = Number(!!b.is_primary) - Number(!!a.is_primary);
    if (primary !== 0) {
      return primary;
    }
    const positionA = a.position ?? Number.MAX_SAFE_INTEGER;
    const positionB = b.position ?? Number.MAX_SAFE_INTEGER;
    if (positionA !== positionB) {
      return positionA - positionB;
    }
    return String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''));
  });
}

@Component({
  selector: 'app-statement-documents',
  templateUrl: './statement-documents.component.html',
  standalone: false,
})
export class StatementDocumentsComponent {
  @Input() attachments: StatementAttachment[] | null = [];
  @Input({ required: true }) surveyId!: number;
  @Input({ required: true }) questionId!: number;

  expanded = false;

  constructor(
    public preview: AttachmentPreviewService,
    private dialog: MatDialog,
  ) {}

  get ordered(): StatementAttachment[] {
    return sortStatementAttachments(this.attachments || []);
  }

  get alwaysVisibleCount(): number {
    const primaries = this.ordered.filter((attachment) => attachment.is_primary).length;
    return Math.max(DEFAULT_VISIBLE_DOCUMENTS, primaries);
  }

  get visible(): StatementAttachment[] {
    return this.expanded ? this.ordered : this.ordered.slice(0, this.alwaysVisibleCount);
  }

  get hiddenCount(): number {
    return Math.max(0, this.ordered.length - this.alwaysVisibleCount);
  }

  open(attachment: StatementAttachment): void {
    const kind = this.preview.kind(attachment);
    if ((kind === 'link' || kind === 'pubmed') && attachment.link) {
      window.open(attachment.link, '_blank', 'noopener');
      return;
    }
    const documents = this.ordered.filter((item) => item.type === 'FILE');
    this.dialog.open(ModalDocumentViewerComponent, {
      data: {
        surveyId: this.surveyId,
        questionId: this.questionId,
        attachments: documents,
        index: Math.max(0, documents.indexOf(attachment)),
      },
      panelClass: 'document-viewer-dialog',
      width: '96vw',
      maxWidth: '96vw',
      height: '94vh',
      autoFocus: false,
      restoreFocus: true,
    });
  }

  trackBy = (_: number, attachment: StatementAttachment): string => `${attachment.type}-${attachment.id}`;
}
